import React from "react";
import styled from "styled-components";
import { ModalTemplate } from "../../templates/ModalTemplate";
import { PropertyForm } from "../form/PropertyForm";
import {
  usePropertyRegistration,
  usePropertyToast,
} from "../../pages/property/hook";
import { Toast } from "../../atoms/Toast";

// Temporary type definition to fix import issue
type PropertyFormData = {
  propertyCode: string;
  propertyName: string;
  propertyCategory: string;
  propertyType: string;
  propertyStatus: string;
  owner: string;
  city: string;
  district: string;
  commune: string;
  price: string;
  size: string;
  landArea: string;
  bedrooms: string;
  bathrooms: string;
  popularPlace: string;
  features: string[];
  linkYoutube: string;
  googleMapLink: string;
  description: string;
  images: File[];
  coverImage: File | string | null;
  isAvailable: boolean;
  isHot: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: PropertyFormData) => void;
  refetchProperties: () => void;
};

export const CreatePropertyModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  refetchProperties
}) => {
  const { toast, showToast, hideToast } = usePropertyToast();
  const { handleRegisterProperty } = usePropertyRegistration(
    showToast,
    refetchProperties,
    onClose
  );
  const handleFormSubmit = async (
    data: PropertyFormData,
    onError: (message: string) => void
  ): Promise<void> => {
    if (!data.coverImage || !(data.coverImage instanceof File)) {
      onError("Cover image is required.");
      return;
    }
    if (!data.images || data.images.length === 0) {
      onError("At least one property image is required.");
      return;
    }

    // Ensure features is an array of integers
    const featureIds: number[] = Array.isArray(data.features)
      ? data.features.map((f: any) => {
        if (typeof f === "object" && f !== null) {
          if ("id" in f) return parseInt(f.id, 10);
          if ("value" in f) return parseInt(f.value, 10);
        }
        return parseInt(f, 10);
      }).filter((id: number) => !isNaN(id)) // Filter out any NaN values
      : [];

    return new Promise<void>((resolve, reject) => {
      handleRegisterProperty(
        {
          property_name: data.propertyName,
          property_category_id: data.propertyCategory,
          property_type_id: data.propertyType,
          property_status: data.propertyStatus,
          owner_id: data.owner,
          city_id: data.city,
          district_id: data.district,
          commune_id: data.commune,
          price: data.price,
          size: data.size,
          land_area: data.landArea,
          bedrooms: data.bedrooms,
          bathrooms: data.bathrooms,
          popular_place_id: data.popularPlace,
          feature_ids: featureIds, // Always send as array (empty or with values)
          youtube_link: data.linkYoutube,
          google_map_link: data.googleMapLink,
          description: data.description,
          property_images: data.images,
          thumbnail_image: data.coverImage as File,
          is_available: data.isAvailable ? "true" : "false",
          is_hot: data.isHot ? "true" : "false",
        },
        (errorMessage) => {
          onError(errorMessage);
          reject(new Error(errorMessage));
        },
        () => {
          onSubmit?.(data);
          resolve();
        }
      );
    });
  };

  return (
    <div>
      {toast.visible && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <ModalTemplate
        isOpen={isOpen}
        title="Create Property"
        onClose={onClose}
        maxWidth="95vw"
      >
        <ResponsiveWrapper>
          <PropertyForm key={isOpen ? 'open' : 'closed'} handleSubmit={handleFormSubmit} />
        </ResponsiveWrapper>
      </ModalTemplate>
    </div>
  );
};

const ResponsiveWrapper = styled.div`
  @media (max-width: 768px) {
    padding: 0 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0 4px;
  }
`;
